import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music } from 'lucide-react';

import { TimeDisplay } from '../TimeDisplay/TimeDisplay';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { PlayPauseButton } from '../PlayPouseButton/PlayPousebutton';
import { SkipButton } from '../SkipButton/SkipButton';
import { ShuffleButton } from '../ShuffleButton/ShuffleButton';
import { LoopButton } from '../LoopButton/LoopButton';
import { VolumeSlider } from '../VolumeSlider/VolumeSlider';
import { PlayerContext } from '../../../../context/PlayerContext';
import { coverArtUrl } from '../../../../config/images';
import { AlbumArt } from '../../cards/AlbumArt';
import './AudioPlayer.css';

/**
 * Now-playing display and transport controls. The actual <audio> element
 * lives in PlayerContext (mounted once, for the app's lifetime) so it
 * survives page navigation - this component just reads playback state from
 * context and renders controls for it.
 */
export function AudioPlayer() {
    const navigate = useNavigate();
    const {
        currentSong,
        isPlaying,
        shuffle,
        toggleShuffle,
        repeatMode,
        cycleRepeat,
        playNext,
        playPrevious,
        currentTime,
        duration,
        togglePlay,
        seek,
        volume,
        setVolume,
    } = useContext(PlayerContext);

    const hasSong = Boolean(currentSong?.getId());
    const art = hasSong ? coverArtUrl(currentSong.getAlbumId()) : undefined;

    return (
        <div className="playerContainer">
            <div className="nowPlayingArt">
                {art ? (
                    <AlbumArt img={art} title={currentSong.getAlbum()} iconSize={32} />
                ) : (
                    <Music size={32} strokeWidth={2.75} />
                )}
            </div>

            <div className="nowPlayingMeta">
                <span className="nowPlayingTitle">
                    {hasSong ? currentSong.getTitle() : 'Nothing playing'}
                </span>
                <span className="nowPlayingArtist">
                    {hasSong ? (
                        <>
                            {currentSong.getArtists().length > 0
                                ? currentSong.getArtists().map((a, i) => (
                                      <span key={a.id}>
                                          {i > 0 && ', '}
                                          <span
                                              className="nowPlayingLink"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/artists/${a.id}`);
                                              }}
                                          >
                                              {a.title}
                                          </span>
                                      </span>
                                  ))
                                : null}
                            {currentSong.getAlbum() && (
                                <>
                                    {' · '}
                                    {currentSong.getAlbumId() ? (
                                        <span
                                            className="nowPlayingLink"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/albums/${currentSong.getAlbumId()}`);
                                            }}
                                        >
                                            {currentSong.getAlbum()}
                                        </span>
                                    ) : (
                                        currentSong.getAlbum()
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        '—'
                    )}
                </span>
            </div>

            <div className="playerProgress">
                <TimeDisplay time={currentTime} start={true} />
                <ProgressBar currentTime={currentTime} duration={duration} onSeek={seek} />
                <TimeDisplay time={duration} />
            </div>

            <div className="playerControls">
                <ShuffleButton active={shuffle} onClick={toggleShuffle} />
                <SkipButton direction="back" onClick={playPrevious} />
                <PlayPauseButton isPlaying={isPlaying} togglePlay={togglePlay} />
                <SkipButton direction="forward" onClick={playNext} />
                <LoopButton mode={repeatMode} onClick={cycleRepeat} />
            </div>

            <div className="playerVolume">
                <VolumeSlider volume={volume} onVolumeChange={setVolume} />
            </div>
        </div>
    );
}
